import { DestroyRef, Directive, HostListener, inject, input } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, EMPTY, finalize, Observable } from 'rxjs';

/**
 * A shared lock so several buttons can be *mutually exclusive* — while any one
 * of them is running, the others are disabled too. Create ONE object in the
 * component and pass the same reference to each button's `[lockGroup]`.
 * Omit `[lockGroup]` entirely for buttons that should run independently.
 *
 * Example (don't allow "Löschen" while "Speichern" is in flight, and vice versa):
 *   // component.ts
 *   formLock: AsyncClickLock = { busy: false };
 *   // template
 *   <button [asyncClick]="save.bind(this)"   [lockGroup]="formLock" [disabled]="s.disabled" #s="asyncClick">Speichern</button>
 *   <button [asyncClick]="delete.bind(this)" [lockGroup]="formLock" [disabled]="d.disabled" #d="asyncClick">Löschen</button>
 */
export interface AsyncClickLock {
  busy: boolean;
}

/**
 * `[asyncClick]` — double-click protection for buttons that fire a POST/PUT/DELETE.
 *
 * ── The problem it solves ────────────────────────────────────────────────────
 * A plain `(click)="save()"` lets an impatient user click twice before the first
 * request comes back, firing the mutation twice (duplicate order, double delete,
 * etc.). Historically every button had to hand-roll its own `busy` boolean guard;
 * almost none did. This directive centralises that guard in ONE place so every
 * button gets it the same way and it's impossible to forget on a new button.
 *
 * ── How you use it (the contract) ────────────────────────────────────────────
 * Replace `(click)="save()"` with `[asyncClick]="save.bind(this)"`, and change
 * the handler so it **returns** the Observable instead of subscribing to it:
 *
 *   // BEFORE — handler subscribes internally, directive can't see when it finishes
 *   save() { this.svc.update(...).subscribe(); }
 *
 *   // AFTER — handler RETURNS the (not-yet-subscribed) Observable
 *   save(): Observable<unknown> { return this.svc.update(...); }
 *
 *   <button [asyncClick]="save.bind(this)" [disabled]="b.disabled" #b="asyncClick">Speichern</button>
 *
 * The `.bind(this)` matters: `[asyncClick]` receives the *function itself* (it is
 * NOT called during template evaluation), and the directive later calls it as a
 * bare `this.action()`. Without `.bind(this)`, `save` would lose its `this` and
 * blow up. For a handler that needs an argument, return a factory instead and call
 * it in the template: `[asyncClick]="onDelete(id)"` where `onDelete(id)` returns
 * `() => this.svc.delete(id)`.
 *
 * Why "return, don't subscribe"? An RxJS Observable is *cold* — the HTTP request
 * does not start until something calls `.subscribe()`. By returning the cold
 * Observable, the handler hands the directive the *ability to start* the request;
 * the directive subscribes exactly once and therefore controls the whole lifecycle
 * (start → busy, finish → reset). If the handler subscribed itself, the directive
 * would only get a Subscription that's already running and could not gate it.
 *
 * ── What it does on each click ───────────────────────────────────────────────
 *  1. If this button is already `disabled` (its own request in flight, its
 *     `lockGroup` busy, or a caller-supplied `[disabled]` condition — see below),
 *     ignore the click. This is the guard.
 *  2. Otherwise mark `busy` (and the `lockGroup`, if any).
 *  3. Call the handler to get the Observable, then `.subscribe()` to start it.
 *  4. `finalize(...)` runs when the request completes, errors, OR the subscription
 *     is torn down — so `busy` is guaranteed to reset in every case (this is the
 *     bit hand-rolled guards routinely got wrong: they reset on success but not on
 *     error). `catchError(() => EMPTY)` keeps an error from bubbling out of the
 *     directive; surface errors to the user inside the handler's own pipe (e.g. via
 *     `CallApiService.callWithToasts$`, which adds the success/error toasts).
 *
 * ── Disabling the button ─────────────────────────────────────────────────────
 * The directive deliberately does NOT host-bind `disabled` itself. Instead it
 * exposes {@link busy} and {@link disabled} and you bind them from the template:
 *
 *   <button [asyncClick]="save.bind(this)" [disabled]="b.disabled" #b="asyncClick">…</button>
 *
 * This is on purpose: a directive host-binding to `disabled` would silently
 * override a form button's own `[disabled]="form.invalid"`, and a host-binding
 * *getter* that reads state changing mid-cycle trips Angular's NG0100 dev check.
 * Binding it in the template lets you compose freely and safely, e.g. a form
 * button: `[disabled]="b.disabled || form.invalid || form.pristine"`.
 *
 * ── The busy state is per-button, for free ───────────────────────────────────
 * Each `[asyncClick]` on the page is its own directive *instance* with its own
 * `busy`. Two buttons in the same component => two independent guards, no keys or
 * shared flags needed. Reach for `[lockGroup]` (above) only when you additionally
 * need buttons to block each other.
 *
 * ── Reading state in the template ────────────────────────────────────────────
 * Export the instance with a template reference variable:
 *   <button [asyncClick]="save.bind(this)" [disabled]="b.disabled" #b="asyncClick">
 *     @if (b.busy) { <span class="spinner-border spinner-border-sm me-1"></span> }
 *     Speichern
 *   </button>
 */
@Directive({
  selector: '[asyncClick]',
  standalone: true,
  exportAs: 'asyncClick', // enables `#b="asyncClick"` to read b.busy / b.disabled in templates
})
export class AsyncClickDirective {
  private readonly destroyRef = inject(DestroyRef);

  /**
   * The handler to run on click. It is a *factory* (a zero-arg function that
   * returns the Observable), NOT the Observable itself — so the request is only
   * created when the user actually clicks, and re-created fresh on every click.
   */
  readonly action = input.required<() => Observable<unknown>>({ alias: "asyncClick" });

  /** Optional shared lock to make several buttons mutually exclusive. See {@link AsyncClickLock}. */
  readonly lockGroup = input<AsyncClickLock>();

  /**
   * True while THIS button's own request is in flight. Read it in the template via
   * `#b="asyncClick"` → `b.busy` to show a spinner on the right button.
   */
  busy = false;

  /**
   * Convenience for the template's `[disabled]` binding: true while this button's
   * request is in flight OR a sibling in the same `lockGroup` is running. Compose
   * with your own conditions, e.g. `[disabled]="b.disabled || form.invalid"`.
   */
  get disabled(): boolean {
    return this.busy || !!this.lockGroup()?.busy;
  }

  @HostListener('click')
  onClick(): void {
    // Guard: drop the click if this button's request is in flight or its lock
    // group is busy. (A caller-supplied `[disabled]` already stops the native
    // click, so we only need to cover our own in-flight state here.)
    if (this.disabled) {
      return;
    }

    this.busy = true;
    const lockGroup = this.lockGroup();
    if (lockGroup) {
      lockGroup.busy = true;
    }

    this.action()()
      .pipe(
        // Keep errors from escaping the directive; the handler's own pipe is
        // responsible for user-facing error handling (toasts).
        catchError(() => EMPTY),
        // Cancel an in-flight request if the button is destroyed mid-request
        // (completes the stream → finalize runs → busy/lock reset).
        takeUntilDestroyed(this.destroyRef),
        // Runs on complete, error, AND unsubscribe → busy always resets.
        finalize(() => {
          this.busy = false;
          const lockGroupValue = this.lockGroup();
          if (lockGroupValue) {
            lockGroupValue.busy = false;
          }
        })
      )
      .subscribe();
  }
}
